/**
 * 계측 타입 정의 (클라이언트 안전)
 *
 * 이 파일은 브라우저에서 안전하게 사용할 수 있습니다.
 * type-graphql 의존성이 없습니다.
 *
 * 서버에서 GraphQL enum 등록이 필요한 경우:
 * import { ... } from "@ict/shared/server"
 */

/** 판정 결과 enum */
export enum JudgmentResultEnum {
  /** 양호 */
  GOOD = "GOOD",
  /** 주의 */
  CAUTION = "CAUTION",
  /** 불량 */
  BAD = "BAD",
  /** 해당없음 */
  NA = "NA",
}

/** 판정 결과 타입 (하위 호환성) */
export type JudgmentResultType = `${JudgmentResultEnum}`;

/** 판정 결과 객체 */
export interface JudgmentResult {
  result: JudgmentResultType;
  discussion: string;
}

/**
 * 모든 계측 스키마에 공통으로 포함되는 필드
 * MeasureController<T extends BaseMeasureFields>의 제약으로 사용
 * @see MeasureCommon.schema.ts의 필드와 동기화
 */
export interface BaseMeasureFields {
  /** 판정 결과 */
  result?: JudgmentResultEnum | null;
  /** 수동 판정 여부 */
  resultManuallySet?: boolean;
  /** 의견/코멘트 */
  discussion?: string;
}

/** 측정 설정 */
export interface MeasureConfig {
  /** 기준값 (정격값, 설계값 등) */
  standard?: number | string;
  /** 하한 임계값 */
  lowerThreshold?: number;
  /** 상한 임계값 */
  upperThreshold?: number;
  /** 주의 하한 (선택) */
  cautionLower?: number;
  /** 주의 상한 (선택) */
  cautionUpper?: number;
  /** 허용 오차 (±%) */
  tolerancePercent?: number;
  /** 허용 오차 (절대값) */
  toleranceAbsolute?: number;
  /** 추가 설정 (타입별 커스텀) */
  extra?: {
    /** 정격전압 (BatteryVoltage용) */
    ratedVoltage?: number;
    /** 주파수대역 */
    frequencyBand?: string;
    /** 셀 수 (ChargingVoltage용) */
    cellCount?: number;
    /** 배터리 타입 */
    batteryType?: string;
    /** 기타 */
    [key: string]: unknown;
  };
}

/** 24개 계측 타입 enum - Measures.schema.ts의 필드명과 일치 */
export enum MeasureType {
  // 전기 (7개)
  AC_VOLTAGE = "acVoltage",
  DC_VOLTAGE = "dcVoltage",
  GROUND_RESISTANCE = "groundResistance",
  TERMINATION_RESISTANCE = "terminationResistance",
  CHARGING_VOLTAGE = "chargingVoltageCurrent",
  BATTERY_VOLTAGE = "batteryVoltage",
  CLOCK_LINE_VOLTAGE = "clockLineVoltage",
  // 통신환경 (5개)
  LINK_SPEED = "linkSpeed",
  OPTICAL_LEVEL = "opticalLevel",
  TX_OUTPUT = "txOutput",
  OUTPUT_TERMINAL_LEVEL = "outputTerminalLevel",
  IO_SIGNAL_LEVEL = "ioSignalLevel",
  // 전파환경 (3개)
  RSRP_TERRESTRIAL = "rsrpTerrestrial",
  RSRP_FM_RADIO = "rsrpFmRadio",
  RSRP_SATELLITE = "rsrpSatellite",
  // 무선신호 (2개)
  EMERGENCY_BELL_SENSITIVITY = "emergencyBellSensitivity",
  REMOTE_METERING_SENSITIVITY = "remoteMeteringSensitivity",
  // 전계강도 (2개)
  COAXIAL_FIELD_STRENGTH = "coaxialFieldStrength",
  WIRELESS_FIELD_STRENGTH = "wirelessFieldStrength",
  // RF카드 (2개)
  RF_MAGNETIC_FIELD = "rfMagneticField",
  RF_RECOGNITION_DISTANCE = "rfRecognitionDistance",
  // 기타 (3개)
  TEMPERATURE_HUMIDITY = "temperatureHumidity",
  BATTERY_SPECIFIC_GRAVITY = "batterySpecificGravity",
  MANHOLE_TOXIC_GAS = "manholeToxicGas",
}

/**
 * MeasureType 값(필드명)을 GraphQL enum 키로 변환
 * 예: "acVoltage" -> "AC_VOLTAGE"
 */
export function measureTypeValueToGraphQLKey(value: MeasureType | string): string {
  // MeasureType enum의 값->키 역매핑
  const valueToKey: Record<string, string> = {
    acVoltage: "AC_VOLTAGE",
    dcVoltage: "DC_VOLTAGE",
    groundResistance: "GROUND_RESISTANCE",
    terminationResistance: "TERMINATION_RESISTANCE",
    chargingVoltageCurrent: "CHARGING_VOLTAGE",
    batteryVoltage: "BATTERY_VOLTAGE",
    clockLineVoltage: "CLOCK_LINE_VOLTAGE",
    linkSpeed: "LINK_SPEED",
    opticalLevel: "OPTICAL_LEVEL",
    txOutput: "TX_OUTPUT",
    outputTerminalLevel: "OUTPUT_TERMINAL_LEVEL",
    ioSignalLevel: "IO_SIGNAL_LEVEL",
    rsrpTerrestrial: "RSRP_TERRESTRIAL",
    rsrpFmRadio: "RSRP_FM_RADIO",
    rsrpSatellite: "RSRP_SATELLITE",
    emergencyBellSensitivity: "EMERGENCY_BELL_SENSITIVITY",
    remoteMeteringSensitivity: "REMOTE_METERING_SENSITIVITY",
    coaxialFieldStrength: "COAXIAL_FIELD_STRENGTH",
    wirelessFieldStrength: "WIRELESS_FIELD_STRENGTH",
    rfMagneticField: "RF_MAGNETIC_FIELD",
    rfRecognitionDistance: "RF_RECOGNITION_DISTANCE",
    temperatureHumidity: "TEMPERATURE_HUMIDITY",
    batterySpecificGravity: "BATTERY_SPECIFIC_GRAVITY",
    manholeToxicGas: "MANHOLE_TOXIC_GAS",
  };
  return valueToKey[value] ?? value;
}

/** 기존 CHECK_RESULTS와 매핑 */
export const JUDGMENT_TO_CHECK_RESULT: Record<JudgmentResultType, string> = {
  GOOD: "GOOD",
  CAUTION: "GOOD", // 주의는 일단 양호로 저장 (UI에서 별도 표시)
  BAD: "BAD",
  NA: "NA",
};

/** UI 표시용 상수 */
export const JUDGMENT_DISPLAY = {
  GOOD: { label: "양호", symbol: "○", color: "green" },
  CAUTION: { label: "주의", symbol: "△", color: "yellow" },
  BAD: { label: "불량", symbol: "×", color: "red" },
  NA: { label: "해당없음", symbol: "/", color: "gray" },
} as const;

/**
 * 판정 결과별 코멘트 오버라이드
 * DB에서 가져온 MeasureDiscusItem을 기반으로 생성
 */
export interface DiscussionOverrides {
  /** 공통 코멘트 (항상 포함) */
  common?: string;
  /** 적합(good) 판정 시 코멘트 */
  good?: string;
  /** 요주의(caution) 판정 시 코멘트 */
  caution?: string;
  /** 조치필요(bad) 판정 시 코멘트 */
  bad?: string;
  /** 해당없음(na) 시 코멘트 */
  na?: string;
}

/**
 * 계측 판정 코멘트 항목 (클라이언트 안전)
 * DB의 MeasureDiscus.schema와 동일 구조이나 type-graphql 의존성 없음
 */
export interface MeasureDiscusItem {
  key: string;
  common?: string;
  good?: string;
  caution?: string;
  bad?: string;
  na?: string;
}
